<template>
  <div class="select-lang">
    <v-btn
      ref="selectLangBtn"
      @click="toggleSelect"
      :rounded="isRounded"
      :fab="!isRounded"
      :x-small="!isRounded"
      color="#219653"
      class="select-lang-header"
    >
      <slot name="header">
        <div><img
          :src="flagUrl"
          width="24"
          @error="onImgError"
        ></div>
        <span>{{ firstLang.locale }}</span>
        <div>
          <v-icon :class='{"rotate": isOpen}' class="select-lang-header__toggle">mdi-chevron-down</v-icon>
        </div>
      </slot>
    </v-btn>
    <v-card
      v-if="isOpen"
      ref="selectLangList"
      class="select-lang-list"
      :class="{ 'select-lang-list--up': openUpward }"
    >
      <div @click="toggleSelect" class="select-lang-list__wrapper-icon">
        <v-icon>mdi-close</v-icon>
      </div>
      <div class="select-lang-list__wrapper" @click.stop="() => {}">
        <span>Localization</span>
        <div class="select-lang-list__wrapper-locales">
          <div v-for="([_, locale], idx) in orderedLanguages" :key="idx" class="select-lang-list__wrapper-locales-section">
            <span>{{ locale[0].title }}</span>
            <v-chip-group
              :value="value"
              @change="editLang(value, $event)"
              multiple
              column
              active-class="select-lang-list__wrapper-locales-section-chips--active" class="select-lang-list__wrapper-locales-section-chips">
              <lang-chip
                v-for="(lang) in locale"
                :key="lang.locale"
                :lang="lang"
                :color="'#F2F2F2'"
                :colorText="'black--text'"
              ></lang-chip>
            </v-chip-group>
          </div>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
  import { ORDERED_LOCALES, mapperIsoLangToOurLocal, localeToIsoPrefix, BASE_FLAG_URL, DEFAULT_FLAG } from './mapperIsoLocale'
  import LangChip from '@/components/core/SelectLang/LangChip.vue'

  export default {
    name: 'SelectLang',
    ORDERED_LOCALES,
    emits: [ 'input', 'open', 'addLang', 'deleteLang' ],
    data () {
      return {
        openUpward: false,
      }
    },
    components: {
      LangChip,
    },

    props: {
      isOpen: Boolean,
      value: Array,
      languages: Array,
      isRounded: Boolean,
    },
    watch: {
      isOpen (open) {
        if (!open) {
          this.openUpward = false
          return
        }
        this.$nextTick(() => {
          this.updatePlacement()
        })
      },
    },
    methods: {
      mapperIsoLangToOurLocal,
      onImgError (e) {
        e.target.src = `${BASE_FLAG_URL}${DEFAULT_FLAG}.svg`
      },
      toggleSelect () {
        this.$emit('open', !this.isOpen)
      },
      updatePlacement () {
        const btn = this.$refs.selectLangBtn?.$el
        if (!btn) {
          return
        }

        const panelEl = this.$refs.selectLangList?.$el
        const panelHeight = panelEl?.offsetHeight || 250
        const gap = 45
        const rect = btn.getBoundingClientRect()
        const spaceBelow = window.innerHeight - rect.bottom
        const spaceAbove = rect.top

        this.openUpward = spaceBelow < panelHeight + gap && spaceAbove > spaceBelow
      },
      onViewportChange () {
        if (this.isOpen) {
          this.updatePlacement()
        }
      },
      editLang (value, event) {
        this.$emit('input', event)
        const addLang = event.filter(el => !value.includes(el))
        const delLang = value.filter(el => !event.includes(el))
        const delIndex = value.findIndex(el => el === delLang[0])

        if (addLang.length) {
          this.$emit('addLang', addLang)
        } else {
          this.$emit('deleteLang', [ delLang, delIndex ])
        }
      },
      outsideClickListener (event) {
        if (!this.isOpen) {
          return
        }

        const el = this.$refs.selectLangList.$el
        const btn = this.$refs.selectLangBtn.$el

        if (!el.contains(event.target) && !btn.contains(event.target)) {
          this.toggleSelect()
        }
      },
    },
    mounted () {
      document.addEventListener('click', this.outsideClickListener, true)
      window.addEventListener('resize', this.onViewportChange)
    },
    beforeDestroy () {
      document.removeEventListener('click', this.outsideClickListener, true)
      window.removeEventListener('resize', this.onViewportChange)
    },
    computed: {
      flagUrl () {
        return `${BASE_FLAG_URL}${this.firstLang.flag}.svg`
      },
      formattedLanguages () {
        return this.languages.map((lang) => lang.toUpperCase())
      },
      firstLang () {
        const locale = this.value[0] || 'EN'
        return { flag: this.mapperIsoLangToOurLocal(localeToIsoPrefix(locale)), locale }
      },
      orderedLanguages () {
        let title = null
        const sortedLocales = this.formattedLanguages.reduce((acc, locale) => {
          let localePrefix = localeToIsoPrefix(locale)
          if (!acc[localePrefix]) {
            acc[localePrefix] = []
          }
          title = this.$options.ORDERED_LOCALES.hasOwnProperty(localePrefix) ? this.$options.ORDERED_LOCALES[localePrefix] : 'Languages'

          acc[localePrefix].push({ locale, title, urlFlag: this.mapperIsoLangToOurLocal(localePrefix) })

          return acc
        }, {})

        Object.keys(sortedLocales).forEach((key) => {
          if (!ORDERED_LOCALES[key]) {
            if (!sortedLocales.others) {
              sortedLocales.others = []
            }

            sortedLocales.others.push(...sortedLocales[key])
            delete sortedLocales[key]
          }
        })
        return Object.entries(sortedLocales)
      },
    }
  }
</script>

<style scoped lang="scss">

  .select {
    &-lang {
      position: relative;

      & ::v-deep .v-card {
        min-width: 600px;
      }

      & ::v-deep .v-btn:not(.v-btn--round).v-size--default {
        height: 32px;
        padding: 4px 8px 4px 4px;
      }


      &-header {
        background-color: #219653;
        //min-width: 85px;
        //height: 32px;
        color: rgba(255, 255, 255, 0.87);
        font-weight: 400;
        font-size: 13px;
        display: flex;
        align-items: center;

        & ::v-deep .v-btn__content {
          display: flex;
          gap: 4px;
        }

        & div {
          display: flex;
          align-items: center;
        }

        &__toggle {
          transition: transform .3s ease-in-out !important;
        }

        &__toggle.rotate {
          transform: rotate(180deg);
        }
      }

      &-list {
        position: absolute;
        top: 45px;
        left: 0;
        z-index: 10;
        padding: 20px 15px;
        height: 250px;
        overflow: auto;

        &--up {
          top: auto;
          bottom: 45px;
        }

        &__wrapper {
          position: relative;

          & span {
            font-weight: 600;
            font-size: 16px;

          }

          &-icon {
            position: absolute;
            top: 20px;
            right: 15px;
            height: 24px;
            width: 24px;
            z-index: 3;
          }

          &-locales {
            margin-top: 20px;

            &-section {
              margin-top: 3px;

              & > span {
                color: rgba(0, 0, 0, 0.87);
                font-weight: 500;
                font-size: 14px;
              }

              &-chips {
                &--active {
                  background-color: #6FCF97 !important;

                  & ::v-deep > .v-chip__content {
                    & > span {
                      color: white !important;
                    }
                  }
                }

                & ::v-deep .v-chip__content {
                  display: flex;
                  gap: 4px;
                }

                & div {
                  display: flex;
                  align-items: center;
                }
              }
            }
          }
        }
      }
    }
  }
</style>
